import { useCallback, useEffect, useState } from 'react';
import { holdingsDateRestrictionApi } from '@/services/api';

export const useHoldingsDateRestriction = () => {
  // The backend field is named min_allowed_date (the earliest date requiring full access),
  // which acts as an upper-bound ceiling for non-premium users. Stored here as
  // accessCutoffDate to make the directionality unambiguous at every call-site.
  const [accessCutoffDate, setAccessCutoffDate] = useState<string | null>(null);
  const [restrictionLoaded, setRestrictionLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    holdingsDateRestrictionApi.getRestriction()
      .then(data => {
        if (!cancelled) {
          if (data.restricted && data.min_allowed_date)
            setAccessCutoffDate(data.min_allowed_date);
          setRestrictionLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setRestrictionLoaded(true); /* fail open — no restriction applied */
      });
    return () => { cancelled = true; };
  }, []);

  // Memoised so consumers can safely list these in effect dependency arrays
  // without triggering re-runs on every render (refs stay stable until the
  // cutoff date actually changes).
  /** Returns true when date exceeds the access ceiling (i.e. user lacks permission). */
  const isDateRestricted = useCallback(
    (date: string): boolean => !!accessCutoffDate && !!date && date > accessCutoffDate,
    [accessCutoffDate],
  );

  /** Clamps date to the access ceiling. Returns date unchanged when no restriction applies. */
  const clampDate = useCallback(
    (date: string): string => (isDateRestricted(date) ? accessCutoffDate! : date),
    [isDateRestricted, accessCutoffDate],
  );

  return { accessCutoffDate, restrictionLoaded, isDateRestricted, clampDate };
};
